import React, { useState } from 'react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { storage } from '@/utils/storage';
import { tokenHasPermission } from '@/features/auth/utils/jwtPermissions';
import { deleteFeedback, getFeedbackById, toFeedbacksApiError, updateFeedback } from '../api/feedbacksApi';
import FeedbackModal, { type FeedbackModalMode } from '../components/FeedbackModal';
import FeedbackTable from '../components/FeedbackTable';
import FeedbackToolbar from '../components/FeedbackToolbar';
import { useFeedbacks } from '../hooks/useFeedbacks';
import type { Feedback, UpdateFeedbackInput } from '../types';

const AdminFeedbacksPage: React.FC = () => {
  const { t } = useTranslation();
  const {
    query,
    pageData,
    isInitialLoading,
    isTableLoading,
    loadError,
    setPage,
    setPageSize,
    setSort,
    refresh,
    retry,
  } = useFeedbacks();
  const [modalMode, setModalMode] = useState<FeedbackModalMode>('view');
  const [selectedFeedback, setSelectedFeedback] = useState<Feedback | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pendingFeedbackId, setPendingFeedbackId] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Feedback | null>(null);
  const token = storage.getAccessToken();
  const canUpdate = tokenHasPermission(token, 'FEEDBACK_UPDATE');
  const canDelete = tokenHasPermission(token, 'FEEDBACK_DELETE');

  const openFeedback = async (feedback: Feedback, mode: FeedbackModalMode) => {
    setPendingFeedbackId(feedback.id);
    try {
      const detail = await getFeedbackById(feedback.id);
      setSelectedFeedback(detail);
      setModalMode(mode);
      setIsModalOpen(true);
    } catch (error: unknown) {
      message.error(toFeedbacksApiError(error).message);
    } finally {
      setPendingFeedbackId(null);
    }
  };

  const submitFeedback = async (input: UpdateFeedbackInput) => {
    if (!selectedFeedback) return;
    setIsSubmitting(true);
    try {
      await updateFeedback(selectedFeedback.id, input);
      message.success(t('admin.feedbacks.notices.updated'));
      await refresh();
    } finally {
      setIsSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setPendingFeedbackId(deleteTarget.id);
    try {
      await deleteFeedback(deleteTarget.id);
      message.success(t('admin.feedbacks.notices.deleted'));
      setDeleteTarget(null);
      if (pageData.data.length === 1 && query.page > 1) {
        setPage(query.page - 1);
      } else {
        await refresh();
      }
    } catch (error: unknown) {
      message.error(toFeedbacksApiError(error).message);
    } finally {
      setPendingFeedbackId(null);
    }
  };

  return (
    <div className="mx-auto flex min-h-full w-full min-w-0 max-w-[1600px] flex-col gap-md sm:gap-lg">
      <header className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-h1 text-h1 text-on-surface">{t('admin.feedbacks.title')}</h1>
          <p className="mt-1 text-body-md text-secondary">{t('admin.feedbacks.subtitle')}</p>
        </div>
        <div className="inline-flex w-fit items-center gap-2 rounded-full bg-primary-fixed px-3 py-1.5 font-label-sm text-primary">
          <span className="material-symbols-outlined text-[18px]">reviews</span>
          {t('admin.feedbacks.customer_generated')}
        </div>
      </header>

      <section className="flex min-h-[590px] min-w-0 flex-1 flex-col overflow-hidden rounded-[20px] border border-outline-variant/70 bg-white shadow-sm">
        <div className="border-b border-outline-variant px-4 py-4 sm:px-5">
          <h2 className="font-h2 text-h2 text-on-surface">{t('admin.feedbacks.management_title')}</h2>
          <p className="mt-1 text-body-sm text-secondary">{t('admin.feedbacks.management_description')}</p>
        </div>
        <FeedbackToolbar
          sort={query.sort}
          pageSize={query.size}
          onSortChange={setSort}
          onPageSizeChange={setPageSize}
        />
        {loadError && pageData.data.length > 0 && (
          <div className="flex items-center justify-between gap-3 border-b border-error/20 bg-error-container/35 px-4 py-2 text-body-sm text-error" role="alert">
            <span>{loadError}</span>
            <button type="button" className="shrink-0 font-label-sm text-primary hover:underline" onClick={() => void retry()}>
              {t('admin.feedbacks.actions.retry')}
            </button>
          </div>
        )}
        <FeedbackTable
          feedbacks={pageData.data}
          currentPage={pageData.currentPage}
          totalPages={pageData.totalPages}
          totalElements={pageData.totalElements}
          pageSize={pageData.pageSize}
          isLoading={isTableLoading}
          isInitialLoading={isInitialLoading}
          error={loadError}
          canUpdate={canUpdate}
          canDelete={canDelete}
          pendingFeedbackId={pendingFeedbackId}
          onPageChange={setPage}
          onView={(feedback) => void openFeedback(feedback, 'view')}
          onEdit={(feedback) => void openFeedback(feedback, 'edit')}
          onDelete={setDeleteTarget}
          onRetry={() => void retry()}
        />
      </section>

      <FeedbackModal
        open={isModalOpen}
        mode={modalMode}
        feedback={selectedFeedback}
        isSubmitting={isSubmitting}
        onClose={() => setIsModalOpen(false)}
        onSubmit={submitFeedback}
      />

      {deleteTarget && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/45 p-4" role="presentation" onMouseDown={(event) => {
          if (event.target === event.currentTarget && pendingFeedbackId === null) setDeleteTarget(null);
        }}>
          <section className="w-full max-w-md rounded-[20px] border border-outline-variant bg-white p-5 shadow-xl" role="alertdialog" aria-modal="true" aria-labelledby="delete-feedback-title">
            <div className="flex items-start gap-3">
              <span className="material-symbols-outlined flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-error-container text-error">delete</span>
              <div>
                <h2 id="delete-feedback-title" className="font-h2 text-h2 text-on-surface">{t('admin.feedbacks.delete_dialog.title')}</h2>
                <p className="mt-2 text-body-md text-secondary">{t('admin.feedbacks.delete_dialog.description')}</p>
              </div>
            </div>
            <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button type="button" className="min-h-10 rounded-xl border border-outline-variant px-4 font-label-md text-secondary" disabled={pendingFeedbackId !== null} onClick={() => setDeleteTarget(null)}>
                {t('admin.feedbacks.actions.cancel')}
              </button>
              <button type="button" className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl bg-error px-4 font-label-md text-on-error disabled:opacity-60" disabled={pendingFeedbackId !== null} onClick={() => void confirmDelete()}>
                {pendingFeedbackId !== null && <span className="material-symbols-outlined animate-spin text-[18px]">progress_activity</span>}
                {t(pendingFeedbackId !== null ? 'admin.feedbacks.actions.deleting' : 'admin.feedbacks.actions.delete')}
              </button>
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

export default AdminFeedbacksPage;
